import * as THREE from 'three';

/**
 * Simple static colliders for thrown toys: vertical cylinders (trunks, posts),
 * oriented boxes (house, store) and thin walls (fence). Resolves a moving
 * sphere against them and reports what was hit.
 */
export type Collider =
  | { kind: 'cylinder'; name: string; x: number; z: number; r: number; h: number }
  | { kind: 'box'; name: string; center: THREE.Vector3; yaw: number; half: THREE.Vector3 }
  | { kind: 'wall'; name: string; a: THREE.Vector3; b: THREE.Vector3; h: number; thick: number };

const _p = new THREE.Vector3();
const _n = new THREE.Vector3();
const _q = new THREE.Vector3();

export class Colliders {
  list: Collider[] = [];

  cylinder(name: string, x: number, z: number, r: number, h: number): void {
    this.list.push({ kind: 'cylinder', name, x, z, r, h });
  }
  box(name: string, center: THREE.Vector3, yaw: number, half: THREE.Vector3): void {
    this.list.push({ kind: 'box', name, center: center.clone(), yaw, half: half.clone() });
  }
  wall(name: string, a: THREE.Vector3, b: THREE.Vector3, h: number, thick = 0.06): void {
    this.list.push({ kind: 'wall', name, a: a.clone(), b: b.clone(), h, thick });
  }

  /**
   * Push `pos` out of any collider it overlaps and reflect `vel` about the
   * contact normal. Returns the name of what was hit, or null.
   */
  resolve(pos: THREE.Vector3, vel: THREE.Vector3, radius: number, restitution: number, skip?: string): string | null {
    let hit: string | null = null;
    for (const c of this.list) {
      if (c.name === skip) continue;
      if (c.kind === 'cylinder') {
        if (pos.y - radius > c.h) continue;
        const dx = pos.x - c.x;
        const dz = pos.z - c.z;
        const d = Math.hypot(dx, dz);
        const min = c.r + radius;
        if (d < min && d > 1e-6) {
          _n.set(dx / d, 0, dz / d);
          pos.x = c.x + _n.x * min;
          pos.z = c.z + _n.z * min;
          this.bounce(vel, _n, restitution);
          hit = c.name;
        }
      } else if (c.kind === 'box') {
        // to box-local space
        _p.subVectors(pos, c.center);
        const cs = Math.cos(-c.yaw);
        const sn = Math.sin(-c.yaw);
        const lx = _p.x * cs - _p.z * sn;
        const lz = _p.x * sn + _p.z * cs;
        const ly = _p.y;
        const qx = THREE.MathUtils.clamp(lx, -c.half.x, c.half.x);
        const qy = THREE.MathUtils.clamp(ly, -c.half.y, c.half.y);
        const qz = THREE.MathUtils.clamp(lz, -c.half.z, c.half.z);
        const ddx = lx - qx;
        const ddy = ly - qy;
        const ddz = lz - qz;
        const d = Math.hypot(ddx, ddy, ddz);
        if (d < radius) {
          let nx: number, ny: number, nz: number;
          if (d > 1e-6) {
            nx = ddx / d;
            ny = ddy / d;
            nz = ddz / d;
          } else {
            // inside: push out along the shallowest axis
            const px = c.half.x - Math.abs(lx);
            const py = c.half.y - Math.abs(ly);
            const pz = c.half.z - Math.abs(lz);
            if (px < py && px < pz) [nx, ny, nz] = [Math.sign(lx) || 1, 0, 0];
            else if (py < pz) [nx, ny, nz] = [0, Math.sign(ly) || 1, 0];
            else [nx, ny, nz] = [0, 0, Math.sign(lz) || 1];
          }
          // back to world
          const cs2 = Math.cos(c.yaw);
          const sn2 = Math.sin(c.yaw);
          _n.set(nx * cs2 - nz * sn2, ny, nx * sn2 + nz * cs2);
          _q.set(qx * cs2 - qz * sn2, qy, qx * sn2 + qz * cs2).add(c.center);
          pos.copy(_q).addScaledVector(_n, radius + 0.001);
          this.bounce(vel, _n, restitution);
          hit = c.name;
        }
      } else {
        if (pos.y - radius > c.h) continue;
        // closest point on the segment (ground plane)
        const abx = c.b.x - c.a.x;
        const abz = c.b.z - c.a.z;
        const len2 = abx * abx + abz * abz;
        const t = THREE.MathUtils.clamp(((pos.x - c.a.x) * abx + (pos.z - c.a.z) * abz) / len2, 0, 1);
        const cx = c.a.x + abx * t;
        const cz = c.a.z + abz * t;
        const dx = pos.x - cx;
        const dz = pos.z - cz;
        const d = Math.hypot(dx, dz);
        const min = radius + c.thick / 2;
        if (d < min) {
          if (d > 1e-6) _n.set(dx / d, 0, dz / d);
          else _n.set(-abz, 0, abx).normalize();
          pos.x = cx + _n.x * min;
          pos.z = cz + _n.z * min;
          this.bounce(vel, _n, restitution);
          hit = c.name;
        }
      }
    }
    return hit;
  }

  private bounce(vel: THREE.Vector3, n: THREE.Vector3, restitution: number): void {
    const vn = vel.dot(n);
    if (vn < 0) vel.addScaledVector(n, -(1 + restitution) * vn);
  }
}
